/* API-related constants */

import { ApiUtil } from './api-utils'
import { type FailedResponse } from './types'
import { mapNoticeResponse, mapNoticesResponse } from './response-mappers'
import { type NoticeResponseItem } from './notices-api-types'

const NOTICE_API_PATH = '/notice'

/* Response types */

interface SingleNoticeReturningSuccessResponse {
  succeeded: true
  payload: {
    notice: NoticeResponseItem
  }
}

interface GetNoticesSuccessResponse {
  succeeded: true
  payload: {
    notices: NoticeResponseItem[]
  }
}

export type GetNoticesResponse = GetNoticesSuccessResponse | FailedResponse

export type MarkNoticeAsReadResponse = SingleNoticeReturningSuccessResponse | FailedResponse

/* Export(s) */

/**
 * Backend API for accessing notices as a regular user.
 */
export const NoticesApi = {
  getNotices,
  markAsRead
}

/* API methods */

/**
 * Get notices (for current user).
 * @param token Bearer token.
 */
async function getNotices(token: string): Promise<GetNoticesResponse> {
  const apiDesc = 'get notices request'
  const url = ApiUtil.getApiUrl(NOTICE_API_PATH)

  try {
    const response = await ApiUtil.doGetRequest(url, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }
    const payload: unknown = await response.json()
    const parsedPayload = mapNoticesResponse(payload)
    return {
      succeeded: true,
      payload: {
        notices: parsedPayload
      }
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

/**
 * Mark an existing notice as read by the current user.
 * @param noticeId ID of the notice to be deleted.
 * @param token Bearer token.
 */
async function markAsRead(noticeId: string, token: string): Promise<MarkNoticeAsReadResponse> {
  const apiDesc = 'mark notice as read request'
  const url = ApiUtil.getApiUrl(`${NOTICE_API_PATH}/${noticeId}/mark_as_read`)

  try {
    const response = await ApiUtil.doPutRequest(url, {}, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }

    const payload: unknown = await response.json()
    const parsedPayload = mapNoticeResponse(payload)

    return {
      succeeded: true,
      payload: {
        notice: parsedPayload
      }
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}
